import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowRight16Regular,
  DataTrending24Regular,
  Lightbulb24Regular,
  People24Regular,
  PeopleTeam24Regular,
  Search24Regular,
  TaskListSquareLtr24Regular,
} from "@fluentui/react-icons";
import { useEffect, useState } from "react";

interface SlideData {
  title: string;
  description: string;
  navHint: string;
  icon: JSX.Element;
  image: string;
}

const SLIDES: SlideData[] = [
  {
    title: "Personas",
    description:
      "Your experience adapts to your role. Each persona gets a tailored landing page, navigation menu, and task queue. Switch personas anytime from the settings menu.",
    navHint: "Settings cog \u2192 Switch Persona",
    icon: <People24Regular />,
    image: "/onboarding/01-personas.png",
  },
  {
    title: "AI Priority Engine",
    description:
      "Your AI assistant analyses your workload and surfaces what matters most. Get a personalised daily briefing with case counts, priority recommendations, and revenue-at-risk flags.",
    navHint: "Home page \u2014 your daily briefing",
    icon: <Lightbulb24Regular />,
    image: "/onboarding/02-ai-priority.png",
  },
  {
    title: "Task Details",
    description:
      "Click any task for complete context. The AI brief explains the case, the SOP checklist guides your process, and policy callouts flag mandatory requirements. Activity timeline and related records are always visible.",
    navHint: "Click any task card",
    icon: <TaskListSquareLtr24Regular />,
    image: "/onboarding/03-task-details.png",
  },
  {
    title: "Executive Dashboard",
    description:
      "Revenue KPIs, compliance indicators, debt aging analysis, and exceptions requiring your decision \u2014 consolidated into a single strategic view.",
    navHint: "Dashboard (executive role)",
    icon: <DataTrending24Regular />,
    image: "/onboarding/04-dashboard.png",
  },
  {
    title: "Team Summary",
    description:
      "Monitor SLA adherence, cases closed, quality scores, and team capacity at a glance. Identify overloaded officers and approve or reassign escalated tasks.",
    navHint: "Team page (team leader role)",
    icon: <PeopleTeam24Regular />,
    image: "/onboarding/05-team-summary.png",
  },
  {
    title: "Obligor Search",
    description:
      "Find any entity by name or tax reference with typeahead search. View identity details, registration status, financial summary, and current obligations.",
    navHint: "Obligors in the left navigation",
    icon: <Search24Regular />,
    image: "/onboarding/06-obligor-search.png",
  },
];

const useStyles = makeStyles({
  surface: {
    maxWidth: "960px",
    width: "90vw",
  },
  content: {
    display: "flex",
    gap: tokens.spacingHorizontalXXL,
    alignItems: "center",
    minHeight: "380px",
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
  },
  textArea: {
    flex: "1 1 40%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  description: {
    color: tokens.colorNeutralForeground2,
  },
  navHint: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingInlineStart: tokens.spacingHorizontalM,
    paddingInlineEnd: tokens.spacingHorizontalM,
  },
  navHintIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  imageArea: {
    flex: "1 1 60%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    borderRadius: tokens.borderRadiusLarge,
  },
  slideImage: {
    width: "100%",
    maxHeight: "360px",
    objectFit: "cover",
    objectPosition: "top left",
    borderRadius: tokens.borderRadiusLarge,
    opacity: 0.8,
    borderTopWidth: "1px",
    borderRightWidth: "1px",
    borderBottomWidth: "1px",
    borderLeftWidth: "1px",
    borderTopStyle: "solid",
    borderRightStyle: "solid",
    borderBottomStyle: "solid",
    borderLeftStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  dots: {
    display: "flex",
    justifyContent: "center",
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
  },
  dot: {
    width: "10px",
    height: "10px",
    borderRadius: tokens.borderRadiusCircular,
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    cursor: "pointer",
    padding: 0,
    transition: "background-color 0.15s ease",
  },
  dotActive: {
    backgroundColor: tokens.colorBrandForeground1,
  },
  dotInactive: {
    backgroundColor: tokens.colorNeutralStroke2,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    whiteSpace: "nowrap",
  },
  actionsEnd: {
    marginInlineStart: "auto",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
});

interface GettingStartedDialogProps {
  open: boolean;
  onClose: () => void;
  onDismissForever: () => void;
}

export function GettingStartedDialog({
  open,
  onClose,
  onDismissForever,
}: GettingStartedDialogProps) {
  const styles = useStyles();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentSlide(0);
      setDontShowAgain(false);
    }
  }, [open]);

  const slide = SLIDES[currentSlide];
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === SLIDES.length - 1;

  const handleClose = () => {
    if (dontShowAgain) {
      onDismissForever();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_event, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Getting Started</DialogTitle>
          <Divider />
          <DialogContent>
            <div className={styles.content}>
              <div className={styles.textArea}>
                <div className={styles.titleRow}>
                  <span className={styles.titleIcon}>{slide.icon}</span>
                  <Text as="h3" weight="semibold" size={400}>
                    {slide.title}
                  </Text>
                </div>
                <Text as="p" size={300} className={styles.description}>
                  {slide.description}
                </Text>
                <div className={styles.navHint}>
                  <ArrowRight16Regular className={styles.navHintIcon} />
                  <Text size={200}>{slide.navHint}</Text>
                </div>
              </div>
              <div className={styles.imageArea}>
                <img
                  src={slide.image}
                  alt={slide.title}
                  className={styles.slideImage}
                />
              </div>
            </div>
            <div className={styles.dots}>
              {SLIDES.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={mergeClasses(
                    styles.dot,
                    index === currentSlide
                      ? styles.dotActive
                      : styles.dotInactive,
                  )}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <div className={styles.actions}>
              {isLast && (
                <Checkbox
                  label="Don't show this on login"
                  checked={dontShowAgain}
                  onChange={(_, data) => setDontShowAgain(!!data.checked)}
                />
              )}
            </div>
            <div className={styles.actionsEnd}>
              {!isFirst && (
                <Button
                  appearance="subtle"
                  onClick={() => setCurrentSlide((prev) => prev - 1)}
                >
                  Previous
                </Button>
              )}
              {isLast ? (
                <Button appearance="primary" onClick={handleClose}>
                  Get Started
                </Button>
              ) : (
                <>
                  <Button
                    appearance="primary"
                    onClick={() => setCurrentSlide((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                  <Button appearance="subtle" onClick={handleClose}>
                    Close
                  </Button>
                </>
              )}
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
